import type { ReactNode } from 'react'
import { SettingsCheck } from '../../../ui/SettingsParts'
import {
  GROUP_BYS,
  collapseAll,
  counterText,
  expandAll,
  toggleEdgeType,
  type GroupBy,
  type MapGroupState,
  type MapView,
} from './grouping'

/**
 * Bara a doua a hartii ADM: casetele si filtrele. Tot ce se schimba aici se
 * aplica PE CLIENT, din `grouping.ts` - nu se cere nimic din nou de la server.
 *
 * Un rand, ca bara de deasupra: gruparea si butoanele ei in stanga, filtrele
 * la mijloc, numaratoarea in dreapta. Felurile de legatura sunt bife, cate una
 * pe felul care chiar exista in graful de acum - o bifa pentru ceva ce nu e pe
 * harta n-ar filtra nimic.
 *
 * Bara de sus a hartii are DOUA randuri, nu trei: pagina isi pune aici bifa
 * „Include gone" (`filters`), numaratoarea desenului (`stats`) si butoanele
 * Redraw / Export / PDF / Excel (`children`), ca harta sa inceapa mai sus.
 */
export function MapControls({
  state,
  view,
  onChange,
  filters,
  stats,
  children,
}: {
  state: MapGroupState
  view: MapView | null
  onChange: (next: MapGroupState) => void
  filters?: ReactNode
  stats?: ReactNode
  children?: ReactNode
}) {
  const groups = view?.groups ?? []
  const edgeTypes = view?.edgeTypes ?? []
  const allTypes = edgeTypes.map((t) => t.type)
  const shownTypes = new Set(state.edge_types.filter((t) => allTypes.includes(t)))
  const typeShown = (type: string) => shownTypes.size === 0 || shownTypes.has(type)
  const collapsedCount = view ? view.boxes.filter((b) => b.collapsed).length : 0

  return (
    <div className="adm-bar adm-map-controls" id="mapControls">
      <div className="adm-field">
        <label htmlFor="mapGroup">Group by</label>
        <select
          className="form-control input-sm"
          id="mapGroup"
          value={state.group}
          onChange={(e) => onChange({ ...state, group: e.target.value as GroupBy, collapsed: [] })}
        >
          {GROUP_BYS.map((g) => (
            <option key={g.value} value={g.value}>
              {g.label}
            </option>
          ))}
        </select>
      </div>

      {state.group !== 'none' ? (
        <span className="adm-map-groupbtns">
          <button
            type="button"
            className="btn btn-default btn-sm"
            id="mapCollapseAll"
            title="Fold every box into a single node"
            disabled={groups.length === 0 || collapsedCount === groups.length}
            onClick={() => onChange(collapseAll(state, groups))}
          >
            <i className="fas fa-compress-alt"></i> Collapse all
          </button>
          <button
            type="button"
            className="btn btn-default btn-sm"
            id="mapExpandAll"
            title="Open every box"
            disabled={state.collapsed.length === 0}
            onClick={() => onChange(expandAll(state))}
          >
            <i className="fas fa-expand-alt"></i> Expand all
          </button>
          <span className="adm-map-hint">Click a box to fold or open it</span>
        </span>
      ) : null}

      <span className="adm-check">
        <SettingsCheck
          id="mapHideWorkstations"
          title="Leave out desktops, laptops, printers, phones and other end-user devices"
          checked={state.hide_workstations}
          onChange={(on) => onChange({ ...state, hide_workstations: on })}
          label="Hide workstations & peripherals"
        />
      </span>

      <span className="adm-check">
        <SettingsCheck
          id="mapCriticalOnly"
          title="Only items with an open alarm, Important or Critical items, and components of high or critical business applications"
          checked={state.critical_only}
          onChange={(on) => onChange({ ...state, critical_only: on })}
          label="Critical only"
        />
      </span>

      {filters}

      {edgeTypes.length > 1 ? (
        <span className="adm-map-edgetypes" id="mapEdgeTypes">
          <span className="adm-map-edgetypes-title">Links</span>
          {edgeTypes.map((t) => (
            <span className="adm-check" key={t.type}>
              <SettingsCheck
                id={`mapEdgeType-${t.type || 'other'}`}
                title={`${t.count} ${t.count === 1 ? 'link' : 'links'}`}
                checked={typeShown(t.type)}
                /* Ultima bifa ramasa nu se poate scoate: o harta fara nicio
                   linie nu e un filtru, e o panza goala. */
                disabled={typeShown(t.type) && (shownTypes.size === 1 || allTypes.length === 1)}
                onChange={() => onChange(toggleEdgeType(state, t.type, allTypes))}
                label={t.label}
              />
            </span>
          ))}
        </span>
      ) : null}

      <span className="adm-spacer"></span>

      <span className="adm-count adm-map-stats">
        {stats}
        {stats ? <span className="adm-map-stats-sep" aria-hidden="true"> · </span> : null}
        <span id="mapShowing">{view ? counterText(view.filtered) : ''}</span>
      </span>

      {children}
    </div>
  )
}

